import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Typewriter } from '../ui';
import { useCopilot } from '../../api/copilot';
import { useStickyBottom } from '../../hooks';
import ConfirmActionCard from './ConfirmActionCard';
import LinksCard from './LinksCard';
import LiveCaption from './LiveCaption';
import RetryCard from './RetryCard';
import RunCard from './RunCard';
import SystemLine from './SystemLine';
import ThinkingStatus, { LONG_TEXT } from './ThinkingStatus';
import { NO_CAPTION, NO_MESSAGES } from './util';

/** The scrolling element's class per surface — each already styles its own body. */
const BODY_CLASS = { dock: 'chatpane-body', drawer: 'drawer-body', inline: 'agent-home-body' };

/** A small spoken-question mark, so a transcript shows which turns were voice. */
function SpokenMark() {
  return (
    <svg className="cp-via" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Spoken">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0M12 17v5" />
    </svg>
  );
}

/**
 * One line of the conversation.
 *
 * A reply revealed by the typewriter carries its whole text for assistive
 * technology in a hidden copy, and the animated copy is hidden from it: the
 * thread is a log region, and a node rewritten forty times a second would be
 * announced in fragments. A line still streaming is marked busy, which asks a
 * screen reader to wait until it settles.
 */
function Line({ m }) {
  if (m.from === 'sys') {
    switch (m.kind) {
      case 'confirm':
        return <ConfirmActionCard message={m} />;
      case 'run':
        return <RunCard message={m} />;
      case 'links':
        return <LinksCard message={m} />;
      case 'retry':
        return <RetryCard message={m} />;
      default:
        return <SystemLine message={m} />;
    }
  }

  const from = m.from === 'usr' ? 'usr' : 'bot';
  const cls = ['msg', from, m.ok === false && 'err', m.streaming && 'streaming', m.via === 'voice' && 'voice']
    .filter(Boolean)
    .join(' ');
  const text = m.text ?? '';

  return (
    <div className={cls} aria-busy={m.streaming || undefined}>
      {from === 'usr' && m.via === 'voice' && <SpokenMark />}
      {m.reveal && from === 'bot' ? (
        <>
          <span className="cp-sr">{text}</span>
          <span aria-hidden="true"><Typewriter text={text} /></span>
        </>
      ) : (
        text
      )}
    </div>
  );
}

/**
 * What the reader should be told the assistant is doing, in one short phrase,
 * for the thread's status region — the only live status on the surface, so a
 * long wait is said here too. Empty when there is nothing worth saying.
 */
function statusPhrase({
  mic, listening, speaking, thinking, waitingLong,
}) {
  if (mic === 'blocked') return 'Microphone blocked';
  if (speaking) return 'The assistant is speaking';
  if (thinking) return waitingLong ? LONG_TEXT : 'The assistant is working on it';
  if (listening) return 'Listening';
  return '';
}

/**
 * The conversation: every line, card and live state, in one scrolling column.
 *
 * `variant` names the surface it sits in — the dock's column, the drawer, or
 * `inline` for the landing screen's in-page assistant — which decides the
 * class of the scrolling element (each surface already styles its own body)
 * and which open flag the scroll pinning follows. The inline surface has no
 * open flag: it is on screen whenever it is rendered.
 *
 * `greeting` is the resting line at the top; `children` render right under it,
 * which is where the drawer puts its starter suggestions — at the head of the
 * conversation they start, rather than wedged between the newest answer and
 * the live status below it.
 *
 * Sending pins the view to the bottom whatever the reader had scrolled to, and
 * so does a confirmation card: both are the thread asking for the reader's eyes
 * right now. That is watched here, from the messages themselves, so a spoken
 * question and a chip pin exactly like a typed one without the composer having
 * to reach into this component.
 */
export default function CopilotThread({ variant = 'dock', greeting = null, children = null }) {
  const {
    messages = NO_MESSAGES, thinking = false, speaking = false, listening = false,
    mic = 'off', caption = NO_CAPTION, waitingLong = false,
  } = useCopilot();
  const open = useSelector((s) => {
    if (variant === 'inline') return true;
    return variant === 'drawer' ? s.ui.drawerOpen : s.ui.chatOpen;
  });
  const list = Array.isArray(messages) ? messages : NO_MESSAGES;
  const last = list[list.length - 1];

  const showThinking = thinking && !speaking && !last?.streaming;
  /* Everything that can grow the column counts as new output: a new line, a
     line growing in place, a card changing state, the live rows appearing. */
  const [bodyRef, pin] = useStickyBottom(
    [list.length, last?.text, last?.card?.status, showThinking, caption?.user, caption?.agent, open],
    { enabled: open },
  );

  const seenLast = useRef(last?.id);
  useEffect(() => {
    if (!last || last.id === seenLast.current) return;
    seenLast.current = last.id;
    if (last.from === 'usr' || last.kind === 'confirm') pin();
  }, [last, pin]);

  const phrase = open ? statusPhrase({
    mic, listening, speaking, thinking, waitingLong,
  }) : '';

  return (
    <div
      className={`${BODY_CLASS[variant] || BODY_CLASS.dock} cp-thread`}
      ref={bodyRef}
    >
      {/* One polite status region per surface, saying what the assistant is
          doing in words. Kept outside the log so a change of state is
          announced once, not read back as a line of the conversation. */}
      <span className="cp-sr" role="status" aria-live="polite">{phrase}</span>
      {greeting && <div className="msg bot cp-greeting">{greeting}</div>}
      {children}
      <div className="cp-log" role="log" aria-live="polite" aria-relevant="additions" aria-label="Conversation">
        {/* Keyed by the message's own id: a streaming reply is replaced in
            place as it grows, and an index key would remount it per chunk. */}
        {list.map((m) => <Line key={m.id} m={m} />)}
      </div>
      <LiveCaption />
      {showThinking && (
        <div className="msg bot wait cp-wait">
          <ThinkingStatus />
        </div>
      )}
    </div>
  );
}
